import { useState, useEffect, useRef, useCallback } from 'react';
import { sendChatMessage } from '../utils/chatUtils';

/**
 * Custom hook for handling chat functionality
 * 
 * @returns {Object} Chat state and methods
 */
export const useChat = (userEmail) => {
  const [chatOpen, setChatOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const chatBodyRef = useRef(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, [messages]);

  const toggleChat = useCallback(() => {
    setChatOpen(prevState => !prevState);
  }, []);

  const handleInputChange = useCallback((e) => {
    setUserInput(e.target.value);
  }, []);

  const handleSend = useCallback(async () => {
    if (!userInput.trim()) return;
    setMessages(prev => [...prev, { sender: 'user', text: userInput }]);
    const currentInput = userInput;
    setUserInput('');
    try {
      // Send message to API with user email
      const data = await sendChatMessage(currentInput, userEmail);
      
      // Add bot response to chat
      setMessages(prev => [...prev, {
        sender: 'bot',
        text: data?.reply || data?.result || "I couldn't find an answer to your question."
      }]);
    } catch (error) {
      setMessages(prev => [...prev, {
        sender: 'bot',
        text: 'There was a network error. Please try again later.'
      }]);
    }
  }, [userInput, userEmail]);

  return {
    chatOpen,
    messages,
    userInput,
    chatBodyRef,
    toggleChat,
    handleInputChange,
    handleSend
  };
};

export default useChat;
